import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import client from "../../lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line,
} from "recharts";
import { Globe, MonitorSmartphone, Users, Clock, Flame, TrendingDown, CalendarDays, ArrowRight } from "lucide-react";

/*
 * Enhanced admin analytics with three tabs:
 *   Audience — Who watches? (geo, device, browser, OS, referrer, new vs returning)
 *   Retention — What's working? (retention curves + drop-off points per video)
 *   Timing — When should I post? (7×24 heatmap + top slots)
 *
 * Wraps the existing "overview" panel from AdminDashboard as tab 0. The
 * overview KPIs / charts are unchanged — only the new panels are added here.
 */

const TABS = [
  { key: "audience", label: "Audience" },
  { key: "retention", label: "Retention" },
  { key: "timing", label: "Timing" },
];

export default function AnalyticsTabbed({ children }) {
  const [tab, setTab] = useState("audience");
  return (
    <div className="space-y-6" data-testid="analytics-tabbed">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Analytics</span>
          <h1 className="text-3xl font-black text-white tracking-tight">Channel Insights</h1>
          <p className="text-xs text-zinc-500 mt-1">Overview below · switch tabs for deeper reads.</p>
        </div>
      </div>

      {/* Preserve the existing overview (KPIs + timeseries + region/device pie) */}
      {children}

      {/* New tabbed section */}
      <div className="pt-6 mt-6 border-t border-white/5">
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mr-3">Deep Insights</span>
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`analytics-tab-${t.key}`}
              className={`h-9 px-4 rounded-full text-xs uppercase tracking-widest font-semibold transition-colors border ${
                tab === t.key
                  ? "bg-[#FF6B00] text-black border-[#FF6B00]"
                  : "bg-zinc-900/60 text-zinc-400 hover:text-white border-white/10"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === "audience" && <AudienceTab />}
        {tab === "retention" && <RetentionTab />}
        {tab === "timing" && <TimingTab />}
      </div>
    </div>
  );
}

/* ---------------- Audience ---------------- */
function AudienceTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => {
    setData(null);
    client.get(`/admin/analytics/audience?days=${days}`).then((r) => setData(r.data));
  }, [days]);
  if (!data) return <SkeletonPanel testid="audience-loading" />;
  const k = data.kpis;
  return (
    <div className="space-y-6" data-testid="audience-tab">
      <div className="flex justify-end">
        <RangeSelect value={days} onChange={setDays} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MiniKpi label="Sessions" value={fmt(k.unique_sessions)} icon={Users} />
        <MiniKpi label="Watch Hours" value={fmt(k.watch_hours)} icon={Clock} />
        <MiniKpi label="Completions" value={fmt(k.completions)} icon={Flame} />
        <MiniKpi label="New" value={fmt(k.new_viewers)} accent="text-emerald-300" />
        <MiniKpi label="Returning" value={fmt(k.returning_viewers)} accent="text-[#FFB800]" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Breakdown title="Top Countries" icon={Globe} rows={data.countries} emptyHint="Waiting for geo signals (Cloudflare-driven)…" />
        <Breakdown title="Referrer Channels" icon={ArrowRight} rows={data.referrers} emptyHint="Once viewers arrive from TikTok / IG etc. they'll show here." />
        <Breakdown title="Devices" icon={MonitorSmartphone} rows={data.devices} />
        <Breakdown title="Browsers" rows={data.browsers} />
        <Breakdown title="Operating Systems" rows={data.oses} />
        <Breakdown title="Cities" rows={data.cities} />
      </div>
    </div>
  );
}

function Breakdown({ title, icon: Icon, rows = [], emptyHint }) {
  const total = rows.reduce((s, r) => s + (r.views || 0), 0);
  return (
    <div className="rounded-xl bg-zinc-900/60 border border-white/5 p-5" data-testid={`breakdown-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-[#FFB800]" />}
        <h3 className="text-xs uppercase tracking-[0.3em] text-white font-bold">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500">{emptyHint || "No data yet."}</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r, i) => {
            const pct = total > 0 ? Math.round((r.views / total) * 100) : 0;
            return (
              <li key={i} className="flex items-center gap-3 text-sm">
                <span className="flex-1 text-zinc-300 truncate">{r.label || "—"}</span>
                <span className="w-16 text-right text-zinc-500 text-xs">{fmt(r.views)} views</span>
                <div className="w-24 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#FF6B00] to-[#FFB800]" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-10 text-right text-[10px] uppercase tracking-widest text-[#FFB800]">{pct}%</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ---------------- Retention ---------------- */
function RetentionTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { client.get("/admin/analytics/retention?limit=25").then((r) => setRows(r.data)); }, []);
  if (!rows) return <SkeletonPanel testid="retention-loading" />;
  if (rows.length === 0) {
    return <EmptyState title="No retention data yet" hint="Once a few viewers watch, drop-off points will appear here." testid="retention-empty" />;
  }
  return (
    <div className="space-y-4" data-testid="retention-tab">
      {rows.map((r) => (
        <RetentionCard key={r.video_id} row={r} />
      ))}
    </div>
  );
}

function RetentionCard({ row }) {
  const curve = row.curve.map((c) => ({ ...c, remaining: Number(c.remaining) }));
  return (
    <Link
      to={`/admin/analytics/${row.video_id}`}
      className="block rounded-xl bg-zinc-900/60 border border-white/5 hover:border-[#FF6B00]/50 transition-colors p-5"
      data-testid={`retention-row-${row.video_id}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
        <h3 className="text-white font-bold flex-1 min-w-0 truncate">{row.title}</h3>
        <span className="text-[10px] uppercase tracking-widest text-[#FFB800]">{row.type}</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 h-32">
          <ResponsiveContainer>
            <LineChart data={curve} margin={{ top: 6, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#232323" />
              <XAxis dataKey="pct" tickFormatter={(v) => `${v}%`} stroke="#666" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} stroke="#666" tick={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{ backgroundColor: "#0a0a0a", border: "1px solid #333", fontSize: 12 }}
                labelFormatter={(v) => `at ${v}% of video`}
                formatter={(v) => [`${v}% still watching`, ""]}
              />
              <Line type="monotone" dataKey="remaining" stroke="#FF6B00" strokeWidth={2} dot={{ r: 3, fill: "#FFB800" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-2 gap-3 content-start">
          <MiniStat label="Sessions" value={fmt(row.sessions)} />
          <MiniStat label="Avg Watched" value={`${row.avg_watch_pct}%`} />
          <MiniStat label="Drop @" value={`${row.biggest_drop_at}%`} icon={TrendingDown} />
          <MiniStat label="Drop Size" value={`-${row.biggest_drop_pct}%`} icon={TrendingDown} />
        </div>
      </div>
    </Link>
  );
}

/* ---------------- Timing ---------------- */
function TimingTab() {
  const [data, setData] = useState(null);
  useEffect(() => { client.get("/admin/analytics/timing?days=60").then((r) => setData(r.data)); }, []);
  const maxCell = useMemo(() => {
    if (!data) return 0;
    let m = 0;
    for (const row of data.heatmap) for (const c of row) if (c.watch_hours > m) m = c.watch_hours;
    return m || 1;
  }, [data]);
  if (!data) return <SkeletonPanel testid="timing-loading" />;
  const perDay = data.per_day;
  return (
    <div className="space-y-6" data-testid="timing-tab">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl bg-zinc-900/60 border border-white/5 p-5">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays className="w-4 h-4 text-[#FFB800]" />
            <h3 className="text-xs uppercase tracking-[0.3em] text-white font-bold">Weekly Rhythm — Watch Hours by Day × Hour (UTC)</h3>
          </div>
          {/* Heatmap */}
          <div className="overflow-x-auto">
            <table className="border-separate" style={{ borderSpacing: 2 }}>
              <thead>
                <tr>
                  <th className="w-10"></th>
                  {Array.from({ length: 24 }).map((_, h) => (
                    <th key={h} className="text-[9px] text-zinc-600 font-normal w-6">{h % 3 === 0 ? h : ""}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.heatmap.map((row, wd) => (
                  <tr key={wd}>
                    <td className="text-[10px] uppercase tracking-widest text-zinc-500 pr-2">{data.day_names[wd]}</td>
                    {row.map((c) => {
                      const intensity = maxCell > 0 ? c.watch_hours / maxCell : 0;
                      const bg = intensity === 0
                        ? "rgba(255,255,255,0.03)"
                        : `rgba(255, 107, 0, ${0.15 + intensity * 0.85})`;
                      return (
                        <td key={c.hour} title={`${data.day_names[wd]} ${c.hour}:00 · ${c.views} views · ${c.watch_hours}h`}
                            className="w-6 h-6 rounded" style={{ backgroundColor: bg }} />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500">Darker = more watch time. Times shown in UTC.</p>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl bg-zinc-900/60 border border-white/5 p-5">
            <h3 className="text-xs uppercase tracking-[0.3em] text-white font-bold mb-3">Peak Slots (UTC)</h3>
            {data.top_slots.length === 0 ? (
              <p className="text-sm text-zinc-500">Not enough data yet.</p>
            ) : (
              <ul className="space-y-2">
                {data.top_slots.map((s, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="text-white font-mono">{s.label}</span>
                    <span className="text-[#FFB800] text-xs">{s.watch_hours}h</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-xl bg-zinc-900/60 border border-white/5 p-5">
            <h3 className="text-xs uppercase tracking-[0.3em] text-white font-bold mb-3">Watch Hours by Day</h3>
            <div className="h-40">
              <ResponsiveContainer>
                <BarChart data={perDay} margin={{ top: 6, right: 6, left: -25, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#232323" />
                  <XAxis dataKey="day" stroke="#666" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#666" tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ backgroundColor: "#0a0a0a", border: "1px solid #333", fontSize: 12 }} />
                  <Bar dataKey="watch_hours" fill="#FF6B00" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Shared helpers ---------------- */
function RangeSelect({ value, onChange }) {
  const opts = [7, 14, 30, 60, 90];
  return (
    <div className="flex items-center gap-2" data-testid="range-select">
      {opts.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          data-testid={`range-${o}`}
          className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${
            value === o ? "bg-[#FFB800] text-black border-[#FFB800]" : "text-zinc-400 border-white/10 hover:text-white"
          }`}
        >
          {o}d
        </button>
      ))}
    </div>
  );
}

function MiniKpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-lg bg-zinc-900/60 border border-white/5 p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`mt-1 text-2xl font-black text-white ${accent || ""}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-lg bg-black/40 border border-white/5 p-3">
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-zinc-500">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="text-lg font-bold text-white">{value}</div>
    </div>
  );
}

function SkeletonPanel({ testid }) {
  return <div data-testid={testid} className="h-40 rounded-xl bg-zinc-900/40 border border-white/5 animate-pulse" />;
}

function EmptyState({ title, hint, testid }) {
  return (
    <div className="rounded-xl bg-zinc-900/60 border border-white/5 p-8 text-center" data-testid={testid}>
      <h3 className="text-white font-bold">{title}</h3>
      <p className="text-sm text-zinc-500 mt-1">{hint}</p>
    </div>
  );
}

function fmt(n) {
  if (n === null || n === undefined) return "0";
  if (typeof n !== "number") return String(n);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n * 100) / 100);
}
